import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DailyDto } from 'src/api/FiretrolApiClient';
import { useJobs } from 'src/hooks/useJobs';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { formatDate } from 'src/utils';

interface IDailyTableProps {
  dailys: DailyDto[];
  loading?: boolean;
  openInNewTab?: boolean;
}

const getColumns = (): MRT_ColumnDef<DailyDto>[] => [
  {
    accessorKey: 'dailyNumber',
    header: 'Daily',
    size: 250,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 150,
    Cell: ({ cell }) => {
      const status = cell.getValue<string>();
      return (
        <Badge variant="light" color="blue">
          {status.toUpperCase()}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'date',
    accessorFn: (row) => formatDate(row.date),
    header: 'Date',
    size: 150,
  },
  {
    accessorKey: 'createdBy.fullName',
    accessorFn: (row) => row.createdBy?.fullName || '',
    header: 'Created By',
    size: 150,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 250,
  },
];

export const DailyTable: React.FC<IDailyTableProps> = ({
  dailys,
  loading = false,
  openInNewTab = false,
}) => {
  const navigate = useNavigate();
  const { selectedJob } = useJobs();

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'dailys-table',
    columns: columns,
    defaultSorting: [{ id: 'date', desc: true }],
  });

  const tableExports = useTableExports<DailyDto>('daily-reports', (data) => {
    return data.map((item) => ({
      ['Job Name']: selectedJob?.name,
      ['Job Number']: selectedJob?.jobNumber,
      ['Daily Number']: item.dailyNumber,
      ['Status']: item.status?.toUpperCase() || '',
      ['Date']: formatDate(item.date),
      ['Created By']: item.createdBy?.fullName || '',
      ['Description']: item.description || '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      enableClickToCopy: false,
      columns: columns,
      data: dailys,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const daily = row.original;
        return (
          <Group>
            <ViewActionIcon
              onClick={() => {
                if (openInNewTab) {
                  window.open(
                    `/jobs/${daily.jobId}/dailys/view/${daily.id}`,
                    '_blank',
                  );
                } else {
                  navigate(`/jobs/${daily.jobId}/dailys/view/${daily.id}`);
                }
              }}
            />
          </Group>
        );
      },
    },
    getRowId: (row) => String(row.id),
    totalCount: dailys.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return <>{table}</>;
};
